"use client";

import { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Calendar } from "lucide-react";

interface Props {
  /** Attempt to re-reserve the same slot. Returns true if successful. */
  onRetry: () => Promise<boolean>;
  /** Give up this slot and let the customer pick a new one. */
  onPickNew: () => void;
}

/**
 * Shown when a 10-minute slot hold expires mid-checkout. Offers two
 * paths: re-reserve the same time (may fail if it just got taken) or
 * jump back to the date picker. Non-dismissable without picking one
 * — the customer shouldn't be able to continue on a stale hold.
 */
export default function SlotReservationExpiredModal({ onRetry, onPickNew }: Props) {
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Focus the primary action on mount + trap focus inside the modal
  // until the customer makes a choice. Without this, screen-readers
  // announce the modal but keep focus on the page behind it.
  useEffect(() => {
    primaryRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const tryAgain = async () => {
    setRetrying(true);
    setRetryFailed(false);
    const ok = await onRetry().catch(() => false);
    setRetrying(false);
    if (!ok) setRetryFailed(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="slot-expired-title">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div ref={dialogRef} className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <RefreshCw className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 id="slot-expired-title" className="text-lg font-semibold text-brand-dark">
              Your time slot hold expired
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              We held your chosen time for 10 minutes while you checked out.
              It&apos;s now released and another customer could book it.
            </p>
          </div>
        </div>

        {retryFailed && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2">
            That slot was just taken. Please pick another time.
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            ref={primaryRef}
            onClick={tryAgain}
            disabled={retrying || retryFailed}
            className="w-full rounded-lg bg-brand-dark text-white text-sm font-medium py-2.5 hover:bg-brand-gold transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {retrying ? "Checking availability…" : "Try holding this slot again"}
          </button>
          <button
            onClick={onPickNew}
            className="w-full rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium py-2.5 hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            Pick a different time
          </button>
        </div>
      </div>
    </div>
  );
}
